// src/pages/Profile.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Flex,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Dashboard/Sidebar';
import { resetPassword } from '../api/authService';

function Profile() {
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  if (!user) {
    return null; // This shouldn't happen due to ProtectedRoute, but just in case
  }
  
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Determine badge color based on user role
  const getBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'expert':
        return 'purple';
      default:
        return 'green';
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate password form
  const validateForm = () => {
    const newErrors = {};

    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
    }

    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle password reset submission
  const handlePasswordReset = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      await resetPassword(passwordForm.currentPassword, passwordForm.newPassword);
      
      toast({
        title: 'Password Reset Successful',
        description: 'Your password has been updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Reset form and close modal
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      onClose();
      
    } catch (error) {
      toast({
        title: 'Password Reset Failed',
        description: error.response?.data?.detail || 'An error occurred while resetting password.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <Flex direction="row" h="100vh">
      {/* Collapsible Sidebar Component */}
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
      <Container maxW="container.md" py={8}>
        <VStack spacing={6} align="stretch">
          <Heading size="xl">User Profile</Heading>

          <Box p={5} shadow="md" borderWidth="1px" borderRadius="md">
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Heading size="md">{user.name}</Heading>
                <Badge colorScheme={getBadgeColor(user.role)}>{user.role}</Badge>
              </HStack>

              <Divider />

              <Text>
                <strong>User ID:</strong> {user.name}
              </Text>

              <Text>
                <strong>Access Level:</strong> {' '}
                {user.role === 'admin' ? 'Full Access' :
                  user.role === 'expert' ? 'Expert Access' : 'Standard Access'}
              </Text>

              <Text>
                <strong>Email:</strong> {' '}
                {user.email}
              </Text>

              <Divider />

              <HStack justify="space-between">
                <Heading size="sm">Security Settings</Heading>
                <Button colorScheme="blue" size="sm" onClick={onOpen}>
                  Change Password
                </Button>
              </HStack>

              {/* Additional user information could be fetched from an API here */}
            </VStack>
          </Box>
        </VStack>
      </Container>

      {/* Password Reset Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.currentPassword}>
                <FormLabel>Current Password</FormLabel>
                <Input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                  placeholder="Enter your current password"
                />
                <FormErrorMessage>{errors.currentPassword}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.newPassword}>
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  placeholder="Enter your new password (min 6 characters)"
                />
                <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.confirmPassword}>
                <FormLabel>Confirm New Password</FormLabel>
                <Input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm your new password"
                />
                <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handlePasswordReset}
              isLoading={isLoading}
              loadingText="Updating..."
            >
              Update Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default Profile;